import { assertNever } from '../../../common/helper';
import { CardType, CardValue } from '../logic-types';

type CardIdParams = string | number | [cardValue: number, cardType: CardType];

export class CardId {
  public readonly id: string;
  public readonly cardValue: CardValue;
  public readonly cardType: CardType;

  public get value(): CardValue {
    const value: string = this.id.substring(1);
    return this.parseCardValue(value);
  }

  public get stringValue(): string {
    switch (this.cardValue) {
      case CardValue.Ace:
        return 'A';
      case CardValue.King:
        return 'K';
      case CardValue.Queen:
        return 'Q';
      case CardValue.Jack:
        return 'J';
      case CardValue.Ten:
        return '10';
      case CardValue.Nine:
        return '9';
      case CardValue.Eight:
        return '8';
      case CardValue.Seven:
        return '7';
      case CardValue.Six:
        return '6';
      case CardValue.Five:
        return '5';
      case CardValue.Four:
        return '4';
      case CardValue.Three:
        return '3';
      case CardValue.Two:
        return '2';
      default:
        assertNever(this.cardValue, 'Invalid card value');
    }
  }

  public get type(): CardType {
    const type = this.id.substring(0, 1);
    switch (type) {
      case 'C':
        return CardType.Club;
      case 'S':
        return CardType.Spade;
      case 'H':
        return CardType.Heart;
      case 'D':
        return CardType.Diamond;
      default:
        throw new Error('Invalid CardType');
    }
  }

  public get rawId(): number {
    return this.cardValue - 1 + this.cardType * 13;
  }

  public get isRed(): boolean {
    return this.cardType === CardType.Heart || this.cardType === CardType.Diamond;
  }

  public get isAce(): boolean {
    return this.cardValue === CardValue.Ace;
  }

  public get isKing(): boolean {
    return this.cardValue === CardValue.King;
  }

  constructor(param: CardIdParams) {
    if (typeof param === 'string') {
      this.id = param;
      this.cardValue = this.value;
      this.cardType = this.type;
    } else if (typeof param === 'number') {
      this.cardValue = ((param % 13) + 1) as CardValue;

      if (param <= 12) {
        this.cardType = CardType.Club;
      } else if (param <= 25) {
        this.cardType = CardType.Spade;
      } else if (param <= 38) {
        this.cardType = CardType.Heart;
      } else if (param <= 51) {
        this.cardType = CardType.Diamond;
      } else {
        throw new Error(`Invalid card type for raw value: ${param}`);
      }

      this.id = `${this.cardTypeToString(this.cardType)}${this.cardValue}`;
    } else if (Array.isArray(param) && param.length === 2 && typeof param[0] === 'number') {
      const [cardValue, cardType] = param;

      if (cardValue < CardValue.Ace || cardValue > CardValue.King) {
        throw new RangeError(`${cardValue}`);
      }

      this.cardValue = cardValue as CardValue;
      this.cardType = cardType;
      this.id = `${this.cardTypeToString(this.cardType)}${this.cardValue}`;
    } else {
      throw new Error('Invalid constructor arguments');
    }
  }

  public equals(other: CardId): boolean {
    return this.id === other.id;
  }

  private parseCardValue(value: string): CardValue {
    const numValue = parseInt(value);
    if (!isNaN(numValue)) {
      return numValue as CardValue;
    }
    throw new Error('Invalid CardValue');
  }

  private cardTypeToString(type: CardType): string {
    switch (type) {
      case CardType.Club:
        return 'C';
      case CardType.Spade:
        return 'S';
      case CardType.Heart:
        return 'H';
      case CardType.Diamond:
        return 'D';
      default:
        assertNever(type, 'CardType not recognized');
    }
  }
}
